/**
 * "Why this matters" detail for the Observations & opportunities list,
 * matched against insight titles (first match wins). Ported from app.py's
 * INSIGHT_MORE / enrich_insights.
 */
const INSIGHT_MORE: [RegExp, string][] = [
  [
    /Universal Analytics/i,
    'UA stopped processing data in 2023; lingering tags add page weight, can double-fire events ' +
      'into a misconfigured GA4 stream, and usually signal an unfinished migration. Audit the ' +
      'dataLayer and remove them during onboarding.'
  ],
  [
    /No GA4 detected/i,
    'Without a measurement baseline there is no source of truth for journey milestones, ROAS, or ' +
      'bidding signals. Confirm whether analytics runs server-side or on a non-Google platform ' +
      'before treating this as absent — then make it the first onboarding fix if it truly is.'
  ],
  [
    /hardcoded with no tag manager/i,
    'Every pixel change requires a code deploy, there is no consent-gating layer, and nobody can ' +
      'QA or version tags. Migrating into a tag manager centralizes governance and unlocks ' +
      'Consent Mode and server-side tagging later.'
  ],
  [
    /Server-side tagging infrastructure detected/i,
    'This is a strong signal-health posture. In onboarding, confirm which events route through ' +
      "the server container, who administers it, and request access — it's where conversion " +
      'quality is won or lost.'
  ],
  [
    /No server-side tagging detected/i,
    'Browser-side tracking keeps losing signal to ITP, ad blockers, and consent declines. ' +
      'Server-side collection recovers conversion match quality, which directly feeds every ' +
      "platform's bidding algorithm. A roadmap item, not a day-one blocker."
  ],
  [
    /No consent management platform/i,
    "Ad platforms increasingly require consent signals (e.g. Google's EEA enforcement) and US " +
      'state privacy laws keep expanding. Retrofitting consent later forces tag rework across ' +
      'every platform at once — far cheaper to resolve before media scales.'
  ],
  [
    /no Google Consent Mode/i,
    "Without Consent Mode, Google can't model conversions for users who decline consent, so " +
      'reported conversions undercount and EEA audience features degrade. Enabling Consent Mode ' +
      'v2 through the existing CMP restores modeled conversions.'
  ],
  [
    /Headless \/ JS-framework/i,
    "Single-page apps don't fire traditional pageviews on route changes — tags need " +
      'history-change triggers, and default tag-manager setups silently under-report. QA every ' +
      'journey milestone event against real navigation before trusting the numbers.'
  ],
  [
    /Shopify platform/i,
    "Shopify's app ecosystem and checkout extensibility shape how pixels and events are placed. " +
      'Confirm whether the native pixel gateway or a server-side setup is in use, and which apps ' +
      'own which events, to avoid double-counting.'
  ],
  [
    /Enterprise CMS/i,
    'Tag and tracking changes ride engineering release cycles here. A governed tag manager ' +
      'decouples marketing changes from deploys — and any new milestone tracking should be ' +
      'scoped with realistic lead times.'
  ],
  [
    /Google Optimize/i,
    'Optimize sunset in 2023; leftover snippets are dead weight that can still flicker or break ' +
      'page experiences. Remove the references and migrate experimentation to a supported ' +
      'platform.'
  ],
  [
    /Meta Pixel without visible Conversions API/i,
    'Browser-only Meta events lose matches to ad blockers and ITP. The Conversions API (or ' +
      "Shopify's native integration) improves event match quality and attributed performance — " +
      "just ensure event_id deduplication so conversions aren't double-counted."
  ],
  [
    /Session replay present but no testing/i,
    'Replay shows where users struggle but offers no way to act on it systematically. A testing ' +
      'tool closes the loop, turning observed friction into experiments with measurable outcomes.'
  ],
  [
    /pixel endpoint\(s\) observed firing/i,
    'On-the-wire confirmation is stronger evidence than source-code presence. Pixels detected ' +
      'in source but absent here may be consent-gated or fire only deeper in the funnel — ' +
      'verify checkout and lead paths during onboarding.'
  ]
];

/** Attach expandable "why this matters" detail to a scanner or foundation finding. */
export const insightMore = (insight: { title: string; more?: string }): string | undefined => {
  if (insight.more) return insight.more;
  const hit = INSIGHT_MORE.find(([pattern]) => pattern.test(insight.title));
  return hit?.[1];
};
